import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";

// Privacy Protector
import PrivateRoute from "./PrivateRoute";
// import RestrictedRoute from "./RestrictedRoute";
import { useEffect, useRef } from "react";

// Pages
import AttorneyClaimsPage from "./pages/claims/attorney/AttorneyClaimsPage";
import ClaimsPage from "./pages/claims/ClaimsPage";
import NewClaim from "./pages/claims/NewClaim";
import ClaimsDetail from "./pages/claims/detail/Detail";
import FatalErrorPage from "./pages/error/FatalErrorPage";
import LoginPage from "./pages/login/LoginPage";
import AttorneyCasesPage from "./pages/cases/attorney/AttorneyCasesPage";
import ProviderProfilePage from "./pages/profile/attorney/ProviderProfilePage";
import PaymentsPage from "./pages/payments/PaymentsPage";
import ReportsPage from "./pages/reports/ReportsPage";
import AccessDeniedPage from "./pages/error/AccessDeniedPage";
import UsersPage from "./pages/users/UsersPage";
import UserDetail from "./pages/users/detail/UserDetail";

// Providers
import { ClaimDetailsProvider } from "./context-providers/claims-details-provider";
import { ClaimProvider } from "./context-providers/claims-provider";

// Utils
// import { UserRole } from "./utils/consts";
import AttorneyClaimsDetail from "./pages/claims/attorney/detail/Detail";
import RestrictedRoute from "./RestrictedRoute";
import { ClaimsListingTabRoutes, UserRole } from "./utils/consts";
import { AttorneyProvider } from "./context-providers/attorney-provider";
import AttorneyCaseDetail from "./pages/cases/detail/Detail";
import { useAuth } from "./hooks/useAuth";
import ForgotPassword from "./pages/login/ForgotPassword";
import PasswordReset from "./pages/login/PasswordReset";
import AuthRedirect from "./components/AuthRedirect";
import useRefreshCheck from "./hooks/useRefreshCheck";
import { doesUserHaveRoles } from "./utils/helpers";
import ReleaseNotesPage from "./pages/release-notes/ReleaseNotes";

export enum RouterPathMapping {
	Root = "/",
	Login = "/login",
	AuthCallback = "/auth/callback",
	ForgotPassword = "/forgot-password",
	ResetPassword = "/reset-password",
	Error = "/error",
	ClaimsIndexDefault = "/claims",
	ClaimsTab = "/claims/:tab",
	ClaimCreate = "/claim/create",
	ClaimIntake = "/claim/:id/intake",
	ClaimDetail = "/claim/:id/detail",
	Payments = "/payments",
	Reports = "/reports",
	AccessDenied = "/access-denied",
	AttorneyCases = "/attorney/cases",
	ReleaseNotes = "/release-notes",
	// Users Routes
	UsersIndexDefault = "/users",
	UsersTab = "/users/:tab",
	UserDetail = "/users/user/:id",
	// Attorney Routes
	AttorneyClaims = "/attorney/claims",
	Attorney_ClaimDetail = "/attorney/claim/:id/detail",
	Attorney_CaseDetail = "/attorney/case/:id/detail",
	Attorney_ClaimCreate = "/attorney/case/:caseId/create-claim",
	Attorney_ClaimIntake = "/attorney/claim/:id/intake",
	Attorney_Profile = "/attorney/profile"
}

/**
 * Wrapper component for AccessDeniedPage that determines the correct navigation path based on user role
 */
const RoleBasedAccessDenied = () => {
	const { getUser } = useAuth();
	const user = getUser();
	const userRoles = (user?.role?.split(",") as UserRole[]) ?? [];
	const navigateTo = userRoles.includes(UserRole.Provider)
		? RouterPathMapping.AttorneyCases
		: RouterPathMapping.ClaimsIndexDefault;
	return <AccessDeniedPage navigateTo={navigateTo} />;
};

/**
 * Root component that handles authentication checking and redirection
 */
const RootRoute = () => {
	const { getUser } = useAuth();
	const navigate = useNavigate();
	const user = getUser();

	// Use a simpler approach with a single useEffect that runs only once
	useEffect(() => {
		const destination = user
			? user.role?.split(",").includes(UserRole.Provider)
				? RouterPathMapping.AttorneyClaims
				: RouterPathMapping.ClaimsIndexDefault
			: RouterPathMapping.Login;
		// Use React Router navigation instead of window.location
		navigate(destination, { replace: true });
		// Only run this effect once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Return null as this component only handles redirection
	return null;
};

/**
 * Root claims component that handles authentication checking and redirection to the default claims page
 */
const DefaultClaimsRoute = () => {
	const { getUser } = useAuth();
	const user = getUser();
	let navigateTo: string = ClaimsListingTabRoutes.All;

	if (doesUserHaveRoles(user, [UserRole.ClaimsAnalyst])) {
		navigateTo = ClaimsListingTabRoutes.AnalystDiary;
	} else if (doesUserHaveRoles(user, [UserRole.ClaimsManager])) {
		navigateTo = ClaimsListingTabRoutes.Assigned;
	}

	return <Navigate to={navigateTo} replace={true} />;
};

function Router() {
	useRefreshCheck();

	return (
		<BrowserRouter>
			<Routes>
				{/* Public Root Route - Handles authentication checking and redirection */}
				<Route path={RouterPathMapping.Root} element={<RootRoute />} />
				{/* Authenticated Private Routes */}
				<Route element={<PrivateRoute />}>
					{/* Admin Claims Portal Pages */}
					<Route
						element={
							<RestrictedRoute
								roles={[
									UserRole.Admin,
									UserRole.ClaimsAdmin,
									UserRole.ClaimsAnalyst,
									UserRole.ClaimsManager,
									UserRole.ClaimsViewOnly,
									UserRole.ClaimsNetworkManager,
									UserRole.Payments
								]}
							/>
						}>
						{/* Primary Claims Routes */}
						<Route path={RouterPathMapping.ClaimsIndexDefault} element={<DefaultClaimsRoute />} />
						<Route path={RouterPathMapping.ClaimsTab} element={<ClaimsPage />} />
						<Route
							path={RouterPathMapping.ClaimCreate}
							element={
								<ClaimProvider>
									<NewClaim />
								</ClaimProvider>
							}
						/>
						<Route
							path={RouterPathMapping.ClaimIntake}
							element={
								<ClaimProvider>
									<NewClaim />
								</ClaimProvider>
							}
						/>
						<Route
							path={RouterPathMapping.ClaimDetail}
							element={
								<ClaimProvider>
									<ClaimDetailsProvider>
										<ClaimsDetail />
									</ClaimDetailsProvider>
								</ClaimProvider>
							}
						/>
						{/* Users Routes */}
						<Route
							path={RouterPathMapping.UsersIndexDefault}
							element={<Navigate to="/users/legalease" replace={true} />}
						/>
						<Route path={RouterPathMapping.UsersTab} element={<UsersPage />} />
						<Route path={RouterPathMapping.UserDetail} element={<UserDetail />} />
						{/* Payments Routes */}
						<Route path={RouterPathMapping.Payments} element={<PaymentsPage />} />
						{/* Reports Routes - Restricted to ClaimsManager */}
						<Route element={<RestrictedRoute roles={[UserRole.ClaimsManager]} />}>
							<Route path={RouterPathMapping.Reports} element={<ReportsPage />} />
						</Route>
					</Route>

					{/* Attorney Routes */}
					<Route element={<RestrictedRoute roles={[UserRole.Provider]} />}>
						<Route path={RouterPathMapping.AttorneyCases} element={<AttorneyCasesPage />} />
						<Route path={RouterPathMapping.AttorneyClaims} element={<AttorneyClaimsPage />} />
						<Route
							path={RouterPathMapping.Attorney_ClaimCreate}
							element={
								<ClaimProvider>
									<NewClaim />
								</ClaimProvider>
							}
						/>
						<Route
							path={RouterPathMapping.Attorney_ClaimIntake}
							element={
								<ClaimProvider>
									<NewClaim />
								</ClaimProvider>
							}
						/>
						<Route
							path={RouterPathMapping.Attorney_ClaimDetail}
							element={
								<ClaimProvider>
									<ClaimDetailsProvider>
										<AttorneyClaimsDetail />
									</ClaimDetailsProvider>
								</ClaimProvider>
							}
						/>
						<Route
							path={RouterPathMapping.Attorney_CaseDetail}
							element={
								<AttorneyProvider>
									<AttorneyCaseDetail />
								</AttorneyProvider>
							}
						/>
						<Route path={RouterPathMapping.Attorney_Profile} element={<ProviderProfilePage />} />
					</Route>

					<Route path={RouterPathMapping.ReleaseNotes} element={<ReleaseNotesPage />} />
				</Route>
				{/* Azure AD Authentication Callback */}
				<Route path={RouterPathMapping.AuthCallback} element={<AuthRedirect />} />
				{/* Unauthenticated Pages */}
				<Route path={RouterPathMapping.Login} element={<LoginPage />} />
				<Route path={RouterPathMapping.ForgotPassword} element={<ForgotPassword />} />
				<Route path={RouterPathMapping.ResetPassword} element={<PasswordReset />} />
				<Route path={RouterPathMapping.AccessDenied} element={<RoleBasedAccessDenied />} />
				<Route path={RouterPathMapping.Error} element={<FatalErrorPage />} />
			</Routes>
		</BrowserRouter>
	);
}

export default Router;
